// MACHINERY V2 (Stroje) — Servisné správy tab (M2.5).
//
// Spec: data package §4 (Service Reports list) + ticket §5 #4 / §7 fixes 1, 3
// and 5. Reference impl: design_handoff_machinery_v3/machinery-profile-tabs.jsx
// (`SrTab`, `SrStates`); artboards 1000 / 1001 (desktop, both palettes), 1002
// (skeleton + empty + no-results + the ADDED error state), 1012 (phone).
// This file owns the STATES and the pixels. The query, the sort, the machineId
// guard and the per-fetch failure = useSrTabQuery.ts; kebab entries =
// srTabActions.ts, what they do = useSrTabActions.ts, chips/header =
// srTabChrome.tsx, row pixels = srRows.tsx / srChips.tsx (300-line cap).
//
// STATUS FILTERING IS CLIENT-SIDE HERE, AND THAT IS DELIBERATE.
// v1 sends `statusCodes` to the server. This tab does not, for two reasons that
// both come from the same verified fact: the endpoint returns the machine's
// ENTIRE report list in one page. `ServiceReportsQuery.PageSize` defaults to 0,
// and GoToStockServer-machinery/src/GoToStock/Machinery/GoToStock.Machinery.EF/
// Repositories/ServiceReportsRepository.cs lines 51-64 read
// `if (query.PageSize > 0) { return await dbQuery.ToPageAsync(...); }` followed
// by "// Backward compat: when PageSize == 0, return all items as a single page"
// (personally read in this worktree — ticket process rule (a)). Therefore:
//   1. Artboard 1000 draws a COUNT on each of the three chips. Those counts can
//      only be exact if every report is in hand; a server-filtered fetch knows
//      the size of one bucket at a time and would have to guess the others.
//   2. `hasAnyServiceReports` is only trustworthy on an unfiltered fetch
//      (same slice, :116-129 — set ONLY when search, statusCodes and both dates
//      are empty). Never sending statusCodes means every no-search fetch
//      refreshes it, so "this machine has no reports at all" stops drifting.
// The fetch is NOT trimmed by this — it asks for strictly more than v1 did, and
// search + sort stay SERVER-side exactly as v1 has them.
// FIX 5 (SR half): a rejected fetch used to leave the tab blank. The slice does
// record it (customerMachineServiceReportsSlice.ts:130-133) but nothing rendered
// it, and that `error` is shared by all nine thunks in the slice — a failed
// create would light up the list. So the failure is tracked in useSrTabQuery.ts,
// per fetch AND per machineId, from the dispatch's own promise, and drives
// RetryNotice.
//
// THE STATES BELOW HANG OFF THE IDENTITY GUARD, NOT OFF `loading` ALONE (M2.5
// review finding R1). `reports` arrives from useSrTabQuery already gated: it is
// undefined unless the shared app-wide slot provably holds THIS machine's list.
// So "nothing valid for this machine yet" renders the SKELETON, and neither the
// empty nor the no-results copy can be spoken over the PREVIOUS machine's rows —
// which used to stay on screen, with their destructive kebab actions live, under
// the new machine's hero. See useMachineScope.ts: this guard is now the REQUIRED
// pattern for any v2 surface that reads an app-wide slice keyed by machine, not
// an option.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useMemo } from "react";

import accountContext from "../../../../../account/accountContext";
import { useBreakpoint } from "../../../../../shared/hooks/useBreakpoint";
import useTranslation from "../../../../../shared/hooks/useTranslation";
import usePermission from "../../../../../shared/hooks/usePermission";
import { Modules } from "../../../../../core/models/PermissionModels";
import {
  ServiceReportStatusCode,
  ServiceReportsQueryParameter,
} from "../../../models/ServiceReportsModel";
import RetryNotice from "../../feedback/RetryNotice";
import { SkeletonList, TabCard, TabEmpty } from "./tabAtoms";
import { SR_GRID } from "./srChips";
import { SrPhoneCard, SrRow } from "./srRows";
import { SrHeadCell, SrToolbar } from "./srTabChrome";
import { buildSrActions } from "./srTabActions";
import { useSrTabActions } from "./useSrTabActions";
import { useSrTabQuery } from "./useSrTabQuery";
import SrShareDialogV2 from "./SrShareDialogV2";

/** The six sortable columns, in artboard-1000 order. The 7th grid cell is the
 *  kebab and has no header. */
const SR_COLUMNS: {
  key: ServiceReportsQueryParameter;
  labelKey: string;
  align?: "right";
}[] = [
  { key: ServiceReportsQueryParameter.StatusCode, labelKey: "myMachinery:status" },
  { key: ServiceReportsQueryParameter.Title, labelKey: "myMachinery:title" },
  { key: ServiceReportsQueryParameter.Number, labelKey: "myMachinery:number" },
  { key: ServiceReportsQueryParameter.OpenDate, labelKey: "myMachinery:openDate" },
  { key: ServiceReportsQueryParameter.CloseDate, labelKey: "myMachinery:closeDate" },
  { key: ServiceReportsQueryParameter.CostValue, labelKey: "myMachinery:cost", align: "right" },
];

export interface ServiceReportsTabV2Props {
  machineId: string;
}

const ServiceReportsTabV2: React.FC<ServiceReportsTabV2Props> = ({ machineId }) => {
  const { translate } = useTranslation("myMachinery");
  const { isPhone } = useBreakpoint();
  const { canWrite, canAdmin } = usePermission(Modules.Machinery);

  const {
    search,
    status,
    setStatus,
    sortBy,
    sortDesc,
    reports,
    hasAny,
    loading,
    failed,
    handleSearch,
    handleSort,
    clearFilters,
    refetch,
  } = useSrTabQuery(machineId);

  const { handlers, createReport, shareTarget, closeShare } = useSrTabActions(
    machineId,
    refetch
  );

  const counts = useMemo(() => {
    const all = reports || [];
    return {
      all: all.length,
      inProgress: all.filter((r) => r.statusCode === ServiceReportStatusCode.inProgress).length,
      solved: all.filter((r) => r.statusCode === ServiceReportStatusCode.solved).length,
    };
  }, [reports]);

  const visible = useMemo(
    () => (reports || []).filter((r) => status === null || r.statusCode === status),
    [reports, status]
  );

  const gates = { canWrite, canAdmin, currentUserId: accountContext.user?.id };
  const menuLabel = translate("myMachinery:v2.tabs.sr.rowMenu");
  const dirFor = (key: ServiceReportsQueryParameter): "asc" | "desc" | undefined =>
    sortBy === key ? (sortDesc ? "desc" : "asc") : undefined;

  const filtered = !!search || status !== null;
  /** The shared slot provably holds THIS machine's list — everything below
   *  (skeleton, empty, no-results, rows) hangs off it. */
  const ready = reports !== undefined;
  const showSkeleton = !ready && !failed;
  const showEmpty = ready && !loading && !failed && hasAny === false;
  const showNoResults =
    ready && !loading && !failed && hasAny !== false && visible.length === 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
      <SrToolbar
        search={search}
        onSearchChange={handleSearch}
        status={status}
        onStatusChange={setStatus}
        counts={counts}
        visibleCount={visible.length}
      />

      {failed && (
        <RetryNotice
          title={translate("myMachinery:v2.tabs.sr.loadFailed")}
          hint={translate("myMachinery:v2.tabs.loadFailedHint")}
          onRetry={refetch}
          busy={loading}
        />
      )}

      {showSkeleton && (
        <TabCard>
          <SkeletonList rows={4} height={40} />
        </TabCard>
      )}

      {showEmpty && (
        <TabEmpty
          title={translate("myMachinery:noServiceReportsYet")}
          hint={translate("myMachinery:noServiceReportsYetDescription")}
          ctaLabel={translate("myMachinery:createServiceReport")}
          onCta={createReport}
        />
      )}

      {showNoResults && (
        <TabEmpty
          title={translate("myMachinery:noServiceReportsFound")}
          hint={translate("myMachinery:v2.tabs.sr.noResultsHint")}
          clearLabel={filtered ? translate("myMachinery:v2.tabs.clearFilters") : undefined}
          onClear={filtered ? clearFilters : undefined}
        />
      )}

      {visible.length > 0 &&
        (isPhone ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
            {visible.map((r) => (
              <SrPhoneCard
                key={r.number}
                report={r}
                actions={buildSrActions(r, gates, handlers)}
                onOpen={() => handlers.onShowDetails(r)}
                menuLabel={menuLabel}
              />
            ))}
          </div>
        ) : (
          <TabCard overflow="visible">
            <div
              style={{
                display: "grid",
                gridTemplateColumns: SR_GRID,
                gap: 12,
                padding: "8px 14px",
                borderBottom: "1px solid var(--border)",
                minWidth: 0,
              }}
            >
              {SR_COLUMNS.map((col) => (
                <SrHeadCell
                  key={col.key}
                  label={translate(col.labelKey)}
                  dir={dirFor(col.key)}
                  align={col.align}
                  onClick={() => handleSort(col.key)}
                />
              ))}
              <span />
            </div>
            {visible.map((r) => (
              <SrRow
                key={r.number}
                report={r}
                actions={buildSrActions(r, gates, handlers)}
                onOpen={() => handlers.onShowDetails(r)}
                menuLabel={menuLabel}
              />
            ))}
          </TabCard>
        ))}

      {shareTarget && (
        <SrShareDialogV2
          report={shareTarget}
          open
          onOpenChange={(open) => {
            if (!open) closeShare();
          }}
        />
      )}
    </div>
  );
};

export default ServiceReportsTabV2;
